import axios from 'axios';
import useAuthStore from '../stores/authStore';
import authApi from './authApi';

// In production (when VITE_API_URL is empty), use current origin (handled by nginx proxy)
// In development, use localhost:4001
const API_URL = import.meta.env.VITE_API_URL ||
  (import.meta.env.MODE === 'production' ? window.location.origin : 'http://localhost:4001');

// Debug: log the API URL
console.log('API URL:', API_URL);
console.log('VITE_API_URL env:', import.meta.env.VITE_API_URL);
console.log('Mode:', import.meta.env.MODE);

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true, // Important for HttpOnly cookies
  timeout: 30000, // 30 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add access token to all requests
api.interceptors.request.use(
  (config) => {
    const accessToken = useAuthStore.getState().accessToken;
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle 401 errors and refresh token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.data?.code === 'ORGANIZATION_INACTIVE') {
      useAuthStore.getState().logout();
      if (window.location.pathname !== '/login') window.location.href = '/login';
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the access token
        // authApi returns response.data which is { success: true, data: { accessToken } }
        const result = await authApi.refreshAccessToken();
        const { accessToken } = result.data;

        // Update token in store
        useAuthStore.getState().setAccessToken(accessToken);

        // Process queued requests
        processQueue(null, accessToken);

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed - logout user
        processQueue(refreshError, null);
        useAuthStore.getState().logout();

        // Redirect to login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// API Methods
export const getConfig = async () => {
  const response = await api.get('/api/config');
  return response.data;
};

export const getScenarios = async () => {
  const response = await api.get('/api/user/scenarios');
  return response.data.scenarios;
};

export const getScenario = async (id) => {
  const response = await api.get(`/api/scenarios/${id}`);
  return response.data.scenario;
};

export default api;
